import type { FC } from 'react';
import { useState } from 'react';
import { Card, CardHeader, IconButton, Snackbar } from '@material-ui/core';
import NotificationAvatar from './NotificationAvatar';
import { formatMoney, formatMonthDayYear } from '../../utils/utils';
import { PackageNotification } from './types';
import { useNavigate } from 'react-router';
import DeleteForeverIcon from '@material-ui/icons/Close';
import { sortBy } from 'lodash';

import moment from 'moment';
import { Slide, SlideProps } from '@material-ui/core';
import { usePackageNotificationsQuery } from './graphql/packageNotifications.generated';
import { useIgnorePackageNotificationMutation } from './graphql/ignorePackageNotification.generated';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

const PackageNotifications: FC = () => {
  const { data, refetch } = usePackageNotificationsQuery();
  const sortedNotifications = sortBy(data?.getPackageNotifications, 'date');
  const [open, setOpen] = useState(false);
  const [ignoreNotification] = useIgnorePackageNotificationMutation({
    onCompleted: () => {
      setOpen(true);
      refetch();
    }
  });

  const navigate = useNavigate();

  const handleClose = () => setOpen(false);

  const buildTitle = (notification: PackageNotification) => {
    const titleArray: string[] = [];
    if (notification.counterPartyType) {
      const config = {
        partner: `Partner agency: ${notification.counterPartyName}`,
        customer: `Customer: ${notification.counterPartyName}`
      };

      if (config[notification.counterPartyType]) titleArray.push(config[notification.counterPartyType]);
    }

    return titleArray.join(' ');
  };

  const buildSubtitle = (notification: PackageNotification) => {
    const subtitleArray: string[] = [];
    if (notification.type) {
      const config = {
        total: `${notification.remaining ? formatMoney(+notification.remaining) : 'N/A'}`,
        deposit: `${notification.remaining ? formatMoney(+notification.remaining) : 'N/A'} deposit`,
        proforma: `Proforma missing`
      };

      if (config[notification.type]) subtitleArray.push(config[notification.type]);
    }

    if (notification.date) {
      subtitleArray.push(`due by ${formatMonthDayYear(+notification.date)}`);
    }

    return subtitleArray.join(' ');
  };

  const shouldHighlightNotification = (notification: PackageNotification) => {
    console.log(moment(notification.date).format('YYYY-MM-DD'));
    return notification.date ? moment(+notification.date).isBefore(moment(), 'day') : false;
  };

  const renderActions = (notification: PackageNotification) => {
    return notification.type === 'proforma' ? null : (
      <div className="group-hover:opacity-100 opacity-0 text-grey-500 transition-opacity duration-300">
        <IconButton
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await ignoreNotification({ variables: { paymentId: notification.paymentId || '', type: notification.type || '' } });
          }}
          color="primary"
          size="small"
          aria-label="more"
        >
          <DeleteForeverIcon color="action" fontSize="small" />
        </IconButton>
      </div>
    );
  };

  return (
    <div>
      <h1 className="my-6 text-3xl">Notifications</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 md:gap-4">
        {sortedNotifications.map((notification, i) => (
          <Card
            className="hover:cursor-pointer group hover:bg-gray-50 border border-solid border-grey-300"
            onClick={() => navigate(`/packages/${notification.packageId}`)}
            key={i}
          >
            <CardHeader
              avatar={
                notification.type &&
                notification.counterPartyType && (
                  <NotificationAvatar
                    isDelayed={shouldHighlightNotification(notification)}
                    type={notification.type}
                    paymentType={notification.counterPartyType}
                  />
                )
              }
              title={
                <span className="font-bold flex items-center">
                  <span>{buildTitle(notification)}</span>
                </span>
              }
              subheader={<span>{buildSubtitle(notification)}</span>}
              action={renderActions(notification)}
            />
          </Card>
        ))}
      </div>
      <Snackbar
        open={open}
        TransitionComponent={SlideTransition}
        onClose={handleClose}
        autoHideDuration={3000}
        message="Notification deleted"
      />
    </div>
  );
};

export default PackageNotifications;
